/**
 * @workchat/shared-types/admin-bulk-import — Admin Bulk Import (User CSV) 의 cross-package SSOT.
 *
 * 본 모듈은 server (chat-server admin-bulk-import.routes.ts) + frontend (api/admin-bulk-import.ts /
 * AdminBulkImportUserSection / AdminBulkImportHelp) 가 공유하는 단일 정의.
 *
 * 종전 — 같은 14 string literal 이 4 곳에 hardcode (server union / frontend union / CODE_GUIDE
 * Record / 매뉴얼 표). 새 코드 추가 시 4 곳 동시 갱신 필요 + 누락 시 silent drift.
 *
 * 본 cycle (`bulk-import-frontend-types-ssot`) 의 봉합:
 *   1. server / frontend 의 union 을 `BulkImportErrorCode` 로 단일화
 *   2. 한국어 doc (meaning + nextAction) 을 `BULK_IMPORT_CODE_DOC` Record 에서 SSOT
 *   3. KNOWN_BULK_IMPORT_CODES 를 `Object.keys(BULK_IMPORT_CODE_DOC)` 에서 derive
 *   4. 매뉴얼 표 14 row 도 같은 Record 에서 derive (frontend AdminBulkImportHelp)
 *
 * UI 전용 메타 (severity, action, actionHref) 는 frontend 측 CODE_GUIDE Record 가 별도 보유 —
 * shared 는 backend 가 모르는 UI 정보를 포함하지 않는다 (관심사 분리).
 *
 * 다음 단계: chat-server 가 OpenAPI spec 을 export 하면 본 모듈도 codegen 으로 자동 갱신 가능
 * (현재는 manual SSOT). `bulk-import-openapi-generation` cycle 이 별도 epic.
 */

/** server BulkImportRowError.code 의 14 종 — 코드 추가 시 본 union 만 갱신. */
export type BulkImportErrorCode =
  | 'EMAIL_MISSING'
  | 'NAME_MISSING'
  | 'EMAIL_INVALID'
  | 'EMAIL_TOO_LONG'
  | 'NAME_TOO_LONG'
  | 'EMPLOYEE_NUMBER_TOO_LONG'
  | 'ROLE_INVALID'
  | 'PASSWORD_TOO_SHORT'
  | 'PASSWORD_HASH_FAILED'
  | 'DUPLICATE_IN_CSV'
  | 'EMAIL_EXISTS'
  | 'DELETED_EMAIL_EXISTS'
  | 'INSTITUTION_NOT_FOUND'
  | 'FK_VIOLATION'
  | 'CREATE_FAILED';

export interface BulkImportCodeDoc {
  /** 코드의 의미 — 사용자 가시 한국어. 매뉴얼 표 + 기타 doc 의 baseline. */
  meaning: string;
  /** 다음 행동 — admin 이 어떻게 해결할지. 매뉴얼 표 의 후속 조치 컬럼. */
  nextAction: string;
}

/**
 * 15 코드의 한국어 doc (cross-package SSOT).
 *
 * Record<BulkImportErrorCode, BulkImportCodeDoc> 의 exhaustive 강제 — union 확장 시 entry 누락이
 * 컴파일 에러로 잡힘. 누락 침묵 0.
 *
 * declaration 순서 = 매뉴얼 표 표시 순서 (Object.keys 가 V8 에서 declaration order 보장). 사용자
 * 친화 순서 (필수 누락 → 형식 → 길이/role → password → 중복 → 충돌 → DB 오류 → 일반) 로 정렬.
 */
export const BULK_IMPORT_CODE_DOC: Record<BulkImportErrorCode, BulkImportCodeDoc> = {
  EMAIL_MISSING:        { meaning: 'email 컬럼이 비어있습니다 (필수)', nextAction: 'email 컬럼 채워서 재업로드' },
  NAME_MISSING:         { meaning: 'name 컬럼이 비어있습니다 (필수)', nextAction: 'name 컬럼 채워서 재업로드' },
  EMAIL_INVALID:        { meaning: 'email 형식 오류 — RFC + ASCII (consecutive dot/multi-@/시작·끝 dot 차단)', nextAction: 'email 형식 재확인' },
  EMAIL_TOO_LONG:       { meaning: 'email 255자 초과', nextAction: '짧은 email 사용' },
  NAME_TOO_LONG:        { meaning: 'name 50자 초과', nextAction: '짧은 name 사용' },
  EMPLOYEE_NUMBER_TOO_LONG: { meaning: 'employeeNumber 32자 초과', nextAction: '짧은 사원번호 사용' },
  ROLE_INVALID:         { meaning: '허용 외 role (ADMIN/MANAGER/STAFF 외)', nextAction: 'role 컬럼을 ADMIN/MANAGER/STAFF 중 하나로' },
  PASSWORD_TOO_SHORT:   { meaning: 'password 8자 미만', nextAction: '8자 이상 또는 비워두기 (login 차단으로 생성)' },
  PASSWORD_HASH_FAILED: { meaning: 'bcrypt 처리 실패 — password 형식 또는 시스템 부하', nextAction: 'password 길이 (영문 72자 이하) 확인 또는 server 로그 참조' },
  DUPLICATE_IN_CSV:     { meaning: '같은 CSV 안 동일 email 중복', nextAction: 'CSV 안에서 중복 행 제거' },
  EMAIL_EXISTS:         { meaning: '이미 등록된 사용자 (skip 됨, idempotent)', nextAction: '추가 작업 불필요 — 기존 사용자 유지' },
  DELETED_EMAIL_EXISTS: { meaning: '이전 사용자 (탈퇴) 가 동일 email 보유. soft-delete 정책상 즉시 재사용 불가', nextAction: 'hard-delete 후 재등록 또는 다른 email 사용 — 관리자 매뉴얼 참조' },
  INSTITUTION_NOT_FOUND:{ meaning: 'institution 비존재·비활성', nextAction: 'admin 의 institution 설정 확인 또는 운영팀 문의' },
  FK_VIOLATION:         { meaning: 'foreign key 제약 위반 (institution 외 다른 FK)', nextAction: 'server 로그 확인 후 운영팀 문의' },
  CREATE_FAILED:        { meaning: '알 수 없는 server 오류', nextAction: 'server 로그 확인 후 운영팀 문의' },
};

/**
 * 14 코드 set — runtime 검사용. Object.keys(BULK_IMPORT_CODE_DOC) 에서 derive 로 split-brain 차단.
 *
 * frontend (api 또는 UI) 가 unknown code 분기 시 본 set 으로 has 검사. server 가 추가한 코드를
 * frontend 미반영 상태에서는 KNOWN.has(code) === false → UNKNOWN fallback (해당 frontend 책임).
 */
export const KNOWN_BULK_IMPORT_CODES: ReadonlySet<BulkImportErrorCode> = new Set(
  Object.keys(BULK_IMPORT_CODE_DOC) as BulkImportErrorCode[],
);
